import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';

// Props interface for the ChangeSegmentModal component
interface ChangeSegmentModalProps {
  isOpen: boolean; // Controls modal visibility
  onClose: () => void; // Callback to close the modal
  onSuccess: () => void; // Callback triggered on successful segment update
  onSegmentCountRefresh?: () => void; // Callback to refresh segment counts
  selectedCount: number; // Number of selected audience members
  selectedIds: string[]; // Array of IDs for selected audience members
}

// Interface for segment from API
interface ApiSegment {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  rulesJson: any[];
}

// Interface for contact
interface ApiContact {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

// Component for changing the segment of selected audience members
const ChangeSegmentModal: React.FC<ChangeSegmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onSegmentCountRefresh,
  selectedCount,
  selectedIds,
}) => {
  // Access Redux dispatch

  // State for selected segment and description input
  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedSegmentId, setSelectedSegmentId] = useState('');
  const [staticSegments, setStaticSegments] = useState<ApiSegment[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const [existingContacts, setExistingContacts] = useState<ApiContact[]>([]);
  const [checkingMembership, setCheckingMembership] = useState(false);

  // Fetch static segments when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchStaticSegments();
      // Reset selections when modal opens
      setSelectedSegment('');
      setSelectedSegmentId('');
      setValidationError('');
      setExistingContacts([]);
    }
  }, [isOpen]);

  // Check if selected contacts already exist in the segment when segment changes
  useEffect(() => {
    if (selectedSegmentId && selectedIds.length > 0) {
      checkContactsInSegment(selectedSegmentId);
    } else {
      setValidationError('');
      setExistingContacts([]);
    }
  }, [selectedSegmentId, selectedIds]);

  // Fetch only static segments from API
  const fetchStaticSegments = async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    setLoadingSegments(true);

    try {
      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/segments?page=0&size=100&type=STATIC`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const segments = data.content || [];
      setStaticSegments(segments);
    } catch (error) {
      // console.error('❌ Failed to fetch static segments:', error);
      toast.error('Failed to load static segments');
    } finally {
      setLoadingSegments(false);
    }
  };

  // Check which selected contacts already exist in the segment
  const checkContactsInSegment = async (segmentId: string) => {
    if (!segmentId || selectedIds.length === 0) return;

    setCheckingMembership(true);
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      setCheckingMembership(false);
      return;
    }


    try {
      // Fetch all members of the selected segment
      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/segments/${segmentId}/members?size=1000`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const members = data.content || [];

      // Find which selected contacts are already in the segment
      const existing = members.filter((member: ApiContact) =>
        selectedIds.includes(member.id)
      );

      if (existing.length > 0) {
        setExistingContacts(existing);
        const names = existing.map((c: ApiContact) =>
          `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || c.id
        ).join(', ');
        setValidationError(`The following contacts already exist in this segment: ${names}`);
      } else {
        setExistingContacts([]);
        setValidationError('');
      }
    } catch (error) {
      // console.error('❌ Failed to check segment members:', error);
      toast.error('Failed to check segment members. Please try again later.');
      // Don't show error toast here, just log it
    } finally {
      setCheckingMembership(false);
    }
  };

  // Poll job status
  const pollJobStatus = async (jobId: string, maxAttempts = 10) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`${BASE_URL}/v1/audience-insights/bulk/${jobId}`, {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        // Check if job is complete
        if (data.status === 'COMPLETED' || data.status === 'SUCCESS') {
          toast.success(`Successfully moved ${selectedCount} contact${selectedCount > 1 ? 's' : ''} to "${selectedSegment}"`);

          // Call onSuccess callback
          onSuccess();

          if (onSegmentCountRefresh) {
            setTimeout(() => {
              onSegmentCountRefresh();
            }, 1000);
          }

          // Close modal
          onClose();
          setSelectedSegment('');
          setSelectedSegmentId('');
          setIsProcessing(false);
          setValidationError('');
          setExistingContacts([]);
          return;
        } else if (data.status === 'FAILED' || data.status === 'ERROR') {
          toast.error('Failed to move contacts to segment. Please try again.');
          setIsProcessing(false);
          return;
        }

        // Wait before polling again
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;

      } catch (error) {
        // console.error('❌ Failed to check job status:', error);
        toast.error('Failed to check job status. Please try again later.');
        attempts++;
        if (attempts === maxAttempts) {
          toast.error('Job is taking longer than expected. Please check back later.');
          setIsProcessing(false);
        }
      }
    }

    toast.warning('Job is taking longer than expected. Your contacts will be moved shortly.');
    setIsProcessing(false);
  };

  // Handles segment change for selected audience members
  const handleChange = async () => {
    // Prevent action if no segment is selected
    if (!selectedSegment || !selectedSegmentId) return;

    // Prevent action if there are validation errors
    if (validationError) {
      toast.warning('Please resolve the validation issues before proceeding.');
      return;
    }

    setIsProcessing(true);
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication required');
      setIsProcessing(false);
      return;
    }

    try {

      // Prepare request body
      const requestBody = {
        selection: {
          type: 'IDS',
          ids: selectedIds
        },
        actions: [
          {
            op: 'MOVE_TO_SEGMENT',
            segmentId: selectedSegmentId
          }
        ]
      };


      const response = await fetch(`${BASE_URL}/v1/audience-insights/bulk/actions`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const jobId = data.jobId;

      if (!jobId) {
        throw new Error('No job ID received');
      }

      toast.info('Moving contacts to segment...');

      // Poll for job status
      await pollJobStatus(jobId);

    } catch (error) {
      // console.error('❌ Failed to move contacts:', error);
      toast.error('Failed to move contacts to segment. Please try again.');
      setIsProcessing(false);
    }
  };

  // Return null if modal is not open to prevent rendering
  if (!isOpen) return null;

  // Renders the modal for changing audience segments
  return (
    // Modal overlay with semi-transparent background
    <div className='fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50'>
      {/* Modal content container */}
      <div className='bg-white rounded-lg shadow-lg p-4 w-[480px] max-h-[90vh] overflow-y-auto'>
        {/* Modal title */}
        <h2 className='text-[20px] font-semibold text-[#3F5265] mb-2'>
          Change segment
        </h2>
        {/* Modal description */}
        <p className='text-[16px] text-[#657584] mb-6'>
          Update how this audience is grouped for targeting.
        </p>

        {/* Segment selection dropdown */}
        <label className='block mb-1 font-medium text-[12px] text-[#9FA9B2]'>
          SEGMENT <span className='text-red-500'>*</span>
        </label>
        <select
          value={selectedSegment}
          onChange={(e) => {
            const selectedName = e.target.value;
            const selected = staticSegments.find(seg => seg.name === selectedName);
            setSelectedSegment(selectedName);
            setSelectedSegmentId(selected?.id || '');
            setValidationError('');
            setExistingContacts([]);
          }}
          className='w-full border px-3 py-3 rounded-md mb-2 focus:border-teal-500 focus:border-[2px]'
          disabled={loadingSegments || isProcessing}
        >
          {/* Placeholder option */}
          <option value=''>
            {loadingSegments ? 'Loading segments...' : 'Select segment'}
          </option>
          {/* List static segments from API */}
          {staticSegments.map((seg) => (
            <option key={seg.id} value={seg.name}>
              {seg.name}
            </option>
          ))}
        </select>

        {/* Validation error message */}
        {validationError && (
          <div className='mb-4 p-2 bg-red-50 border border-red-200 rounded-md'>
            <p className='text-sm text-red-600'>
              <span className='font-semibold'>⚠️ {existingContacts.length} contact{existingContacts.length > 1 ? 's' : ''} already in this segment:</span>
              <br />
              <span className='text-xs'>{validationError}</span>
            </p>
          </div>
        )}

        {/* Loading indicator for membership check */}
        {checkingMembership && selectedSegmentId && (
          <div className='mb-4 flex items-center gap-2 text-sm text-gray-500'>
            <span className='inline-block w-4 h-4 border-2 border-t-transparent border-gray-400 rounded-full animate-spin'></span>
            Checking contacts in segment...
          </div>
        )}

        {/* Show message if no static segments found */}
        {!loadingSegments && staticSegments.length === 0 && (
          <p className='text-sm text-gray-500 mb-4'>
            No static segments available. Please create a static segment first.
          </p>
        )}

        {/* Information about selected audience members */}
        {selectedSegment && !validationError && (
          <p className='text-xs text-gray-500 mb-4'>
            {selectedCount} audience member
            {selectedCount > 1 ? 's' : ''} will be grouped under{' '}
            <span className='font-medium'>{selectedSegment}</span>
          </p>
        )}

        {selectedSegment && validationError && (
          <p className='text-xs text-gray-500 mb-4'>
            {selectedCount - existingContacts.length} audience member
            {selectedCount - existingContacts.length > 1 ? 's' : ''} will be grouped under{' '}
            <span className='font-medium'>{selectedSegment}</span>
          </p>
        )}

        {/* Action buttons */}
        <div className='flex justify-end gap-3'>
          {/* Cancel button */}
          <button
            onClick={onClose}
            disabled={isProcessing}
            className='px-4 py-2 border rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Cancel
          </button>
          {/* Change segment button, disabled if no segment selected or processing */}
          <button
            onClick={handleChange}
            disabled={
              !selectedSegment ||
              !selectedSegmentId ||
              loadingSegments ||
              isProcessing ||
              !!validationError ||
              checkingMembership
            }
            className={`px-4 py-2 rounded text-white ${selectedSegment &&
              selectedSegmentId &&
              !loadingSegments &&
              !isProcessing &&
              !validationError &&
              !checkingMembership
              ? 'bg-orange-500 hover:bg-orange-600'
              : 'bg-gray-300 cursor-not-allowed'
              }`}
          >
            {isProcessing ? 'Processing...' : 'Change'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeSegmentModal;